import React from "react";
import { useBranding } from "../context/BrandingContext";

/**
 * The brand lockup, in the two pieces the app actually composes.
 *
 * Kept as two small primitives rather than one <BrandMark> with four className
 * props: the four call sites (desktop sidebar, mobile header, and the login
 * page's artwork panel and its mobile fallback) each use different sizes, gaps
 * and accent colours, and the codebase's style is flat composition at the call
 * site rather than a configurable wrapper.
 */

/** The logo glyph. Renders nothing when the instance has no icon configured,
 *  so a text-only brand doesn't leave a broken-image box in the sidebar. */
export function BrandIcon({ className = "", alt = "" }) {
  const { branding } = useBranding();
  if (!branding.logo_icon_url) return null;
  return <img src={branding.logo_icon_url} alt={alt} className={className} />;
}

/**
 * The wordmark, as text plus an optionally accent-coloured tail -- the shape
 * that lets a brand like "wespeak" + ".ai" keep its coloured suffix. Both
 * halves come from config; `wordmark_accent` may be empty, in which case no
 * second span is rendered at all.
 *
 * `accentClassName` is a prop because the accent colour is contextual, not part
 * of the brand: on the login page's dark artwork panel the tail uses text-glow,
 * everywhere else text-primary.
 */
export function Wordmark({ className = "", accentClassName = "text-primary" }) {
  const { branding } = useBranding();
  return (
    <div className={className}>
      {branding.wordmark_text}
      {branding.wordmark_accent ? (
        <span className={accentClassName}>{branding.wordmark_accent}</span>
      ) : null}
    </div>
  );
}

// Fixed accents after the two brand colours: distinct hues that stay legible
// in both themes and don't collide with the semantic success/danger tokens.
const CHART_ACCENTS = ["#10B981", "#F59E0B", "#8b5cf6", "#EF4444"];

/**
 * Categorical chart palette, led by the instance's own brand colours.
 *
 * Shared by all three charting pages (Dashboard, Utilization, Deal-flow),
 * which each had the same six-colour array with our indigo and cyan hardcoded
 * at the front -- so a customer's rebrand stopped at the edge of every chart.
 *
 * Returns hex strings deliberately, not `rgb(var(--brand-primary))`: Recharts
 * writes these to the SVG `fill` *attribute*, which does not resolve CSS custom
 * properties (only the `style` property does). The API hands us hex already,
 * so there is nothing to convert.
 */
export function useChartPalette() {
  const { branding } = useBranding();
  return [branding.primary, branding.glow, ...CHART_ACCENTS];
}
